// src/utils/stringUtils.js

/**
 * Normaliza um texto para comparação de busca client-side, espelhando a
 * collation utf8mb4_0900_ai_ci do MySQL (accent-insensitive, case-insensitive).
 *
 * Sem isso, os filtros visuais das stores escondem registros que o backend já
 * devolveu: buscar "capitulo" traz "Capítulo" do servidor, mas o `includes()`
 * cru do JS descartaria a linha.
 */
export function normalizeForSearch(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Quebra uma mensagem do sino nos marcadores de negrito da API
 * ("**Maria** aceitou o convite como **Aluno**.") em trechos
 * `{ text, bold }`, para o template montar os <strong> sem v-html.
 * Mensagem sem marcador vira um trecho só, sem negrito.
 */
export function splitBold(message) {
  return String(message ?? "")
    .split("**")
    .map((text, i) => ({ text, bold: i % 2 === 1 }))
    .filter((part) => part.text.length > 0);
}

export function toTitleCase(str) {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Substituir todas as quebras de linha por um padrão único antes de contar o tamanho
//🔍 Explicação:
//Windows usa \r\n → substituímos por \n
//Mac antigos usavam \r → também substituímos por \n
//Linux/macOS modernos já usam apenas \n

export function showLast20Chars(text) {
  if (!text || typeof text !== "string") return "";

  // Evita que barras invertidas sejam interpretadas como caracteres de escape
  const safeText = text.replace(/\\/g, "/");

  return safeText.length <= 20 ? safeText : "..." + safeText.slice(-20);
}

export function formatFilepathForDisplay(filepath) {
  if (!filepath || typeof filepath !== "string") {
    return { label: "", hint: "" };
  }

  // Substitui \ por / para facilitar o split
  const normalized = filepath.replace(/\\/g, "/");

  // Extrai o nome do arquivo (sem diretório)
  const filename = normalized.split("/").pop();

  // Gera o label encurtado
  const label =
    normalized.length <= 20 ? normalized : "..." + normalized.slice(-20);

  return {
    label,
    hint: filename,
  };
}

export function removeTabsAndLineBreaks(text, length) {
  if (typeof text !== "string") {
    text = String(text ?? "");
  }

  // Substitui tabulações e quebras de linha por espaço
  let cleanedText = text.replace(/[\t\n\r\v\f\u2028\u2029]+/g, " ");

  // Remove múltiplos espaços consecutivos
  cleanedText = cleanedText.replace(/\s+/g, " ");

  // Remove espaços no início e no final
  cleanedText = cleanedText.trim();

  // Retorna os primeiros `length` caracteres
  return cleanedText.slice(0, length);
}

export function getSubstring(text, start, end) {
  if (typeof text !== "string") {
    console.warn("⚠️ The first argument must be a string.");
    return "";
  }

  const startIndex = Math.max(0, Math.floor(start));
  let endIndex = text.length;

  if (end !== undefined && end !== null) {
    endIndex = Math.max(0, Math.floor(end));
  }

  return text.substring(startIndex, endIndex);
}

/// Função para remover tags HTML e normalizar quebras de linha
export function stripHtml(html) {
  if (!html) return "";

  let processedHtml = html;

  // Substitui <br> por \n
  processedHtml = processedHtml.replace(/<br\s*\/?>/gi, "\n");

  // Substitui todas ocorrências de </p> (seguido ou não de <p>) por \n
  processedHtml = processedHtml.replace(/<\/p>\s*<p[^>]*>/gi, "\n");
  processedHtml = processedHtml.replace(/<\/p>/gi, "\n");

  // Substitui <div> e </div> por \n
  processedHtml = processedHtml.replace(/<div[^>]*>/gi, "\n");
  processedHtml = processedHtml.replace(/<\/div>/gi, "\n");

  // Remove qualquer tag <p> restante (mantemos só o conteúdo)
  processedHtml = processedHtml.replace(/<p[^>]*>/gi, "");

  // Use DOMParser para remover as demais tags e decodificar entidades HTML
  const doc = new DOMParser().parseFromString(processedHtml, "text/html");
  let text = doc.body.textContent || "";

  // Normaliza espaços
  text = text.replace(/[ \t]+/g, " ");

  // Garante no máximo 2 quebras de linha seguidas
  text = text.replace(/(\r\n|\n|\r){3,}/g, "\n\n");

  // Converte qualquer quebra para \n (caso o sistema use \r\n)
  text = text.replace(/(\r\n|\r)/g, "\n");

  return text.trim();
}

/**
 * Remove quebras de linha "duras" herdadas de extração de PDF (wrap de página
 * gravado como \r\n no meio de frases), preservando quebras legítimas:
 * linhas em branco (parágrafos), itens de lista (•, -, 1., 2.1., a)) e
 * linhas que terminam com pontuação de fim de frase.
 *
 * Regras de junção da linha atual com a próxima:
 * - hífen de translineação ("comis-" + "sões") → une sem espaço, removendo o hífen;
 * - linha sem pontuação terminal E (próxima começa com minúscula/dígito,
 *   OU linha atual termina em vírgula, OU linha atual é longa o bastante
 *   para ser um wrap de largura de página) → une com espaço.
 */
export function unwrapPdfLineBreaks(text) {
  if (!text || typeof text !== "string") return "";

  const lines = text.replace(/\r\n?/g, "\n").split("\n");

  const startsNewBlock = (s) =>
    /^\s*$/.test(s) ||
    /^\s*([•▪◦‣*]|[-–—]\s|\d+(\.\d+)*[.)]\s|[a-z][.)]\s)/i.test(s);

  const endsSentence = (s) => /[.!?;:…]["'”’)\]]*$/.test(s);
  const endsHyphen = (s) => /\p{Ll}-$/u.test(s);
  const startsLower = (s) => /^[\p{Ll}\d]/u.test(s);

  const out = [];
  let current = null;

  for (const raw of lines) {
    if (current === null) {
      current = raw;
      continue;
    }

    const cur = current.replace(/\s+$/, "");
    const curTrim = cur.trim();
    const nextTrim = raw.trim();

    if (curTrim === "" || startsNewBlock(raw)) {
      out.push(current);
      current = raw;
      continue;
    }

    if (endsHyphen(curTrim) && startsLower(nextTrim)) {
      current = cur.replace(/-$/, "") + nextTrim;
      continue;
    }

    if (
      !endsSentence(curTrim) &&
      (startsLower(nextTrim) || /,$/.test(curTrim) || curTrim.length >= 60)
    ) {
      current = cur + " " + nextTrim;
      continue;
    }

    out.push(current);
    current = raw;
  }

  if (current !== null) out.push(current);

  return out.join("\n");
}

export function textToHtml(text) {
  if (!text) return "";
  // Substitui tabulações por espaços não quebráveis
  const processedText = text.replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;");
  // Quebra o texto em linhas e as envolve em <p>
  // Linhas vazias se tornam <p><br></p> para manter a altura do parágrafo no editor
  const str = processedText
    .split("\n")
    .map((line) => `<p>${line}</p>`)
    .join("");
  //alert(str);
  return str;
}

/**
 * Constrói um objeto com três partes para exibição do trecho selecionado
 * com contexto de N caracteres antes e depois, pronto para highlight.
 *
 * @param {string} fullText   - Conteúdo completo do arquivo (já normalizado com \n)
 * @param {number} start      - textRangeStart da nota
 * @param {number} end        - textRangeEnd da nota
 * @param {number} [ctx=100]  - Quantidade de caracteres de contexto antes/depois
 * @returns {{ before: string, excerpt: string, after: string } | null}
 *   Retorna null se os offsets forem inválidos (0/0 ou start >= end).
 */
export function buildHighlightedExcerpt(fullText, start, end, ctx = 100) {
  if (
    typeof fullText !== "string" ||
    typeof start !== "number" ||
    typeof end !== "number" ||
    start >= end ||
    (start === 0 && end === 0)
  ) {
    return null;
  }

  const safeStart = Math.max(0, start);
  const safeEnd = Math.min(fullText.length, end);

  const ctxStart = Math.max(0, safeStart - ctx);
  const ctxEnd = Math.min(fullText.length, safeEnd + ctx);

  return {
    before: ctxStart < safeStart ? fullText.slice(ctxStart, safeStart) : "",
    excerpt: fullText.slice(safeStart, safeEnd),
    after: safeEnd < ctxEnd ? fullText.slice(safeEnd, ctxEnd) : "",
    // true se o contexto foi truncado (havia texto antes/depois além dos 100 chars)
    truncatedBefore: ctxStart > 0,
    truncatedAfter: ctxEnd < fullText.length,
  };
}
